import styled, { css } from 'styled-components';
import { Handle } from 'react-flow-renderer';
import type { NodeState, WorkflowNodeType } from 'flow/service/graph';
import {
  NODE_HEIGHT, NODE_WIDTH, CHILD_NODE_WIDTH, LEFT_CHILD_PADDING,
  NODE_HEIGHT_XL,
} from 'flow/service/graph';
import { StyledFolderHat } from '../FolderNode/styles';
import { isXLNode } from './helpers';


const hoveredStyles = css`
  cursor: pointer;
`;

const focusedStyles = css`
  cursor: default;
  border: 1px solid var(--cl-juicyBlue-500);
  box-shadow:0px 0px 0px 4px color-mix(in srgb, var(--cl-juicyBlue-100) 80%, transparent);
`;

const focusedHoveredStyles = css`
  cursor: pointer;
  border: 1px solid var(--cl-juicyBlue-500);
  box-shadow:0px 0px 0px 4px color-mix(in srgb, var(--cl-juicyBlue-100) 80%, transparent);
`;

const fadedStyles = css`
  cursor: default;
  opacity: 0.35;
`;

const deemphasizedStyles = css`
  opacity: 0.65;
`;

type StyledBasedNodeProps = {
  nodeState: NodeState;
  baseBackgroundColor: string;
  isOutsideCanvas?: boolean;
  isChildNode?: boolean;
  isFolderNode?: boolean;
};

export const StyledBaseNode = styled.div<StyledBasedNodeProps>`
&&& {
  ${({ isOutsideCanvas, isChildNode }) => (isOutsideCanvas
    ? css`width: 100%;`
    : `width: ${isChildNode ? CHILD_NODE_WIDTH : NODE_WIDTH}px;`)}
  height: ${NODE_HEIGHT}px;
  object-fit: contain;
  overflow: hidden;
  color: var(--cl-gray-1100, #191919);
  font-feature-settings: 'clig' off, 'liga' off;
  text-overflow: ellipsis;
  padding: var(--cl-space-1-5);

  background-color: ${({ baseBackgroundColor }) => baseBackgroundColor};

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; // todo - add line height variable
  display: flex;
  border-radius: var(--cl-radius-1);
  align-items: center;
  justify-content: center;
  transition: height 0.2s, width 0.2s ease-in-out;

  /* General base styles based on Node state */
  ${({ nodeState }: StyledBasedNodeProps) => {
    if (nodeState === 'hovered') return hoveredStyles;
    if (nodeState === 'focused') return focusedStyles;
    if (nodeState === 'focused-hovered') return focusedHoveredStyles;
    if (nodeState === 'faded') return fadedStyles;
    if (nodeState === 'deemphasized') return deemphasizedStyles;
    return null;
  }}

  ${({ isFolderNode }: StyledBasedNodeProps) => isFolderNode && StyledFolderHat}
}
`;

export const searchFocusedTextStyles = css`
  background-color: var(--cl-orange-400);
`;

export const searchUnfocusedTextStyles = css`
  background-color: var(--cl-yellow-300);
`;

type StyledNodeTextProps = {
  isChildNode?: boolean;
  isOutsideCanvas?: boolean;
  nodeState: NodeState;
};

export const NODE_TEXT_MAX_WIDTH = 140;

export const StyledNodeText = styled.span<StyledNodeTextProps>`
  ${({ isOutsideCanvas, isChildNode }) => {
    const maxWidth = isChildNode
      ? (NODE_TEXT_MAX_WIDTH - LEFT_CHILD_PADDING)
      : NODE_TEXT_MAX_WIDTH;
    return !isOutsideCanvas && `max-width: ${maxWidth}px;`;
  }}
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  /* General base styles based on Node state */
  ${({ nodeState }) => {
    if (nodeState === 'search-focused') return searchFocusedTextStyles;
    if (nodeState === 'search-unfocused') return searchUnfocusedTextStyles;
    return null;
  }}
`;

export const StyledLeftHandle = styled(Handle)<{
  nodeType?: WorkflowNodeType;
}>`
&&& {
  visibility: hidden;
  left: 1px;
  ${({ nodeType }) => {
    const offset = (nodeType && isXLNode(nodeType)) ? NODE_HEIGHT_XL / 2 : NODE_HEIGHT / 2;
    return `top: ${offset}px;`;
  }}
}
`;

export const StyledRightHandle = styled(Handle)<{
  nodeType?: WorkflowNodeType;
}>`
&&& {
  visibility: hidden;
  right: 1px;
  ${({ nodeType }) => {
    const offset = (nodeType && isXLNode(nodeType)) ? NODE_HEIGHT_XL / 2 : NODE_HEIGHT / 2;
    return `top: ${offset}px;`;
  }}
}
`;

export const StyledEvaluationSetTagNodeIndicator = styled.div`
  width: var(--cl-space-2);
  height: var(--cl-space-2);
  background-color: var(--cl-green-300);
  border-radius: 100%;
  flex: 0 0 var(--cl-space-2);
  z-index: 1;
`;
